import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, CheckCircle, FileText, Search, Users, 
  Lock, ArrowRight, AlertTriangle, Zap, Star, Building
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Investigations = () => {
  const [loading, setLoading] = useState(null);

  const handlePayment = async (tier) => {
    setLoading(tier);
    try {
      const planMap = {
        instant: 'scam_check_basic',
        advanced: 'scam_check_pro',
        professional: 'starter',
        dossier: 'dossier',
        corporate: 'corporate'
      };

      const plan = planMap[tier];
      
      const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: plan,
          originUrl: window.location.origin
        })
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.detail || 'No checkout URL returned');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Unable to process payment. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const services = [
    {
      id: 'instant',
      title: 'Instant Scam Check',
      price: '£4.99',
      description: 'Basic automated fraud risk scan.',
      features: [
        'Fraud Risk Score (0-100)',
        'Basic Risk Indicators',
        'AI Analysis Summary',
        'Instant Results'
      ],
      highlight: false,
      color: 'blue',
      icon: Search,
      cta: 'Run Check'
    },
    {
      id: 'advanced',
      title: 'Advanced Risk Analysis',
      price: '£9.99',
      description: 'Extended fraud intelligence analysis with deeper indicators.',
      features: [
        'Everything in Instant Check',
        'Deep Fraud Indicators',
        'Pattern Analysis',
        'Related Scam Reports',
        'Recommended Actions'
      ],
      highlight: true,
      color: 'cyan',
      icon: Zap,
      cta: 'Get Analysis'
    },
    {
      id: 'professional',
      title: 'Professional Fraud Assessment',
      price: '£49.00',
      description: 'Enhanced OSINT review and deeper entity analysis.',
      features: [
        'OSINT Intelligence Review',
        'Digital Footprint Analysis',
        'Entity Verification',
        'Evidence Summary Report',
        '48-72hr Turnaround'
      ],
      highlight: false,
      color: 'purple',
      icon: Shield,
      cta: 'Start Assessment'
    },
    {
      id: 'dossier',
      title: 'Investigation Dossier',
      price: '£1,250.00',
      description: 'Full manual fraud investigation including evidence preparation and professional report.',
      features: [
        'Full Manual Investigation',
        'Transaction Timeline Reconstruction',
        'Legal-Ready Evidence Pack',
        'Professional PDF Dossier',
        'Expert Consultation Call',
        '5-10 Day Turnaround'
      ],
      highlight: false,
      color: 'orange',
      icon: FileText,
      cta: 'Purchase Dossier'
    },
    {
      id: 'corporate',
      title: 'Corporate Investigation',
      price: '£3,250.00',
      description: 'Deep corporate due diligence investigation including company background intelligence.',
      features: [
        'Corporate Due Diligence',
        'Director & Shareholder Analysis',
        'Company Background Intelligence',
        'Transaction Risk Assessment',
        'Regulatory Compliance Check',
        'Board-Level Report'
      ],
      highlight: false,
      color: 'slate',
      icon: Building,
      cta: 'Begin Investigation'
    }
  ];

  const getColorClasses = (color, type) => {
    const colors = {
      blue: {
        text: 'text-blue-400',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
        button: 'bg-[#1e40af] hover:bg-[#1e3a8a]'
      },
      cyan: {
        text: 'text-cyan-400',
        bg: 'bg-cyan-500/10',
        border: 'border-cyan-500/30',
        button: 'bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400'
      },
      purple: {
        text: 'text-purple-400',
        bg: 'bg-purple-500/10',
        border: 'border-purple-500/30',
        button: 'bg-purple-600 hover:bg-purple-500'
      },
      orange: {
        text: 'text-orange-400',
        bg: 'bg-orange-500/10',
        border: 'border-orange-500/30',
        button: 'bg-orange-600 hover:bg-orange-500'
      },
      slate: {
        text: 'text-slate-300',
        bg: 'bg-slate-500/10',
        border: 'border-slate-500/30',
        button: 'bg-slate-600 hover:bg-slate-500'
      }
    };
    return colors[color]?.[type] || colors.blue[type];
  };

  return (
    <div className="min-h-screen bg-[#0a1628] pt-28 pb-16">
      <Helmet>
        <title>Investigation Services | ActionFraudOne</title>
        <meta name="description" content="Professional fraud investigation services from £4.99. Scam checks, fraud assessments, and comprehensive investigation dossiers." />
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Investigation Services
          </h1>
          <p className="text-gray-400 text-lg">
            From instant scam checks to comprehensive fraud investigations. 
            Choose the service that matches your needs.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
          {services.slice(0, 3).map((service) => (
            <Card 
              key={service.id} 
              className={`bg-[#0d1d32] ${service.highlight ? 'border-cyan-500/50 ring-2 ring-cyan-500/20' : 'border-[#1e40af]/30'} hover:border-[#1e40af]/50 transition-all relative`}
            >
              {service.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <Star size={12} /> POPULAR
                </div>
              )}
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 ${getColorClasses(service.color, 'bg')} rounded-lg flex items-center justify-center`}>
                    <service.icon className={`w-5 h-5 ${getColorClasses(service.color, 'text')}`} />
                  </div>
                  <h3 className="text-xl font-bold text-white">{service.title}</h3>
                </div>
                
                <div className="flex items-baseline gap-1 mb-4">
                  <span className={`text-4xl font-bold ${getColorClasses(service.color, 'text')}`}>
                    {service.price}
                  </span>
                </div>
                
                <p className="text-gray-500 mb-6">{service.description}</p>
                
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-400 text-sm">
                      <CheckCircle className={`w-4 h-4 ${getColorClasses(service.color, 'text')} flex-shrink-0 mt-0.5`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  onClick={() => handlePayment(service.id)}
                  disabled={loading === service.id}
                  className={`w-full ${getColorClasses(service.color, 'button')} text-white font-bold py-6`}
                  data-testid={`service-${service.id}-btn`}
                >
                  {loading === service.id ? 'Processing...' : service.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Premium Services */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Premium Investigation Services</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {services.slice(3).map((service) => (
              <Card 
                key={service.id} 
                className={`bg-[#0d1d32] border-[#1e40af]/30 hover:border-[#1e40af]/50 transition-all`}
              >
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 ${getColorClasses(service.color, 'bg')} rounded-lg flex items-center justify-center`}>
                      <service.icon className={`w-5 h-5 ${getColorClasses(service.color, 'text')}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{service.title}</h3>
                      <span className={`text-2xl font-bold ${getColorClasses(service.color, 'text')}`}>
                        {service.price}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-gray-500 mb-6">{service.description}</p>
                  
                  <ul className="space-y-2 mb-8">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-400 text-sm">
                        <CheckCircle className={`w-4 h-4 ${getColorClasses(service.color, 'text')} flex-shrink-0 mt-0.5`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    onClick={() => handlePayment(service.id)}
                    disabled={loading === service.id}
                    variant="outline"
                    className={`w-full ${getColorClasses(service.color, 'border')} ${getColorClasses(service.color, 'text')} hover:bg-white/5 font-bold py-6`}
                    data-testid={`service-${service.id}-btn`}
                  >
                    {loading === service.id ? 'Processing...' : service.cta}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Need Help Choosing? CTA */}
        <div className="max-w-3xl mx-auto">
          <Card className="bg-gradient-to-r from-[#1e40af]/20 via-cyan-500/10 to-[#1e40af]/20 border-[#1e40af]/30">
            <CardContent className="p-10 text-center">
              <FileText className="w-14 h-14 text-blue-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Need Help Choosing?</h3>
              <p className="text-gray-400 mb-8 max-w-xl mx-auto">
                Not sure which service is right for your case? Our team can help you 
                determine the best approach for your situation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => handlePayment('dossier')}
                  disabled={loading === 'dossier'}
                  className="bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold px-8 py-6 text-lg"
                >
                  {loading === 'dossier' ? 'Processing...' : 'Purchase Investigation Dossier'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
              <p className="text-gray-500 text-sm mt-6">
                Need to discuss first?{' '}
                <a 
                  href="https://wa.me/447386668966?text=I would like to discuss fraud investigation services." 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 underline"
                >
                  Chat on WhatsApp
                </a>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Legal Disclaimer */}
        <div className="max-w-3xl mx-auto mt-16">
          <Card className="bg-[#0d1d32] border-[#1e40af]/20">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                <div className="text-gray-500 text-sm space-y-2">
                  <p className="font-semibold text-gray-400">Important Information</p>
                  <p>
                    ActionFraudOne provides fraud analysis, scam identification and investigative 
                    reporting services. We do not guarantee financial recovery or legal outcomes.
                  </p>
                  <p>
                    Our services are designed to help individuals analyse suspicious communications, websites 
                    and financial offers, and to prepare professional documentation for reporting purposes.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Investigations;
